// schema
// import * as ISchema from '@schema';

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// internal
import * as s from "./LocalizedAddressForm.styles";
import { IProps, IState, IContainer } from "./LocalizedAddressForm.d";
import { localizedAddressFormFragments } from "./LocalizedAddressForm.gql";
import {
  isStateShown,
  isPostalCodeShown,
  getCityLabel,
  getPostalCodeLabel,
} from "./LocalizedAddressForm.util";

// other components
import { CountryPicker } from "@components/country-picker";

// util
// import {
//   maybeGetPrimaryTeamFromOrg,
// } from '@util/team.util';
// import { logger } from '@util/logger.util';
// import { track, TrackingEvents } from '@util/analytics.util';

// constants

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import { TextField } from "@material-ui/core";

class LocalizedAddressFormCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      address: props.initialBillingAddress,
      addressLine1Label: "Address",
      addressLine2Label: "Apartment, suite, etc.",
      stateLabel: "State, county, province, or region",
      postalCodeLabel: "Zip Code",
      cityLabel: "City",
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  // public componentDidMount() {
  //   page(PageEvents.PAYMENT_CONFIRMATION);
  // }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onChangeCallback = () => {
    this.props.onChange(this.state.address);
  };

  private showRelevantFormFields = (country: string) => {
    this.setState(
      (prevState: IState) => ({
        address: {
          ...prevState.address,
          state: isStateShown(country) ? prevState.address.state : "",
          postal_code: isPostalCodeShown(country) ? prevState.address.postal_code : "",
        },
      }),
      this.onChangeCallback,
    );

    // Update the ZIP label to make it more relevant for each country.
    this.setState({ postalCodeLabel: getPostalCodeLabel(country) });

    // Update the 'City' to appropriate name
    this.setState({ cityLabel: getCityLabel(country) });
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onChangeAddressLine1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState(
      (prevState: IState) => ({
        address: { ...prevState.address, line1: value },
      }),
      this.onChangeCallback,
    );
  };

  private onChangeAddressLine2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState(
      (prevState: IState) => ({
        address: { ...prevState.address, line2: value },
      }),
      this.onChangeCallback,
    );
  };

  private onChangeCity = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState(
      (prevState: IState) => ({
        address: { ...prevState.address, city: value },
      }),
      this.onChangeCallback,
    );
  };

  private onChangeState = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState(
      (prevState: IState) => ({
        address: { ...prevState.address, state: value },
      }),
      this.onChangeCallback,
    );
  };

  private onChangePostalCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState(
      (prevState: IState) => ({
        address: { ...prevState.address, postal_code: value },
      }),
      this.onChangeCallback,
    );
  };

  private onChangeCountry = (countryId: string) => {
    this.setState(
      (prevState: IState) => ({
        address: { ...prevState.address, country: countryId },
      }),
      this.onChangeCallback,
    );
    this.showRelevantFormFields(countryId);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderAddressLine1Input = () => {
    const { address, addressLine1Label } = this.state;
    const { classes } = this.props;

    return (
      <div className={classes.billingAddressTextFieldContainer}>
        <TextField
          fullWidth
          InputProps={{
            classes: { input: classes.billingAddressInput, root: classes.billingAddressInputRoot },
          }}
          // label={addressLine1Label}
          InputLabelProps={{ shrink: true }}
          placeholder={addressLine1Label}
          variant="outlined"
          value={address.line1}
          onChange={this.onChangeAddressLine1}
        />
      </div>
    );
  };

  private maybeRenderAddressLine2Input = () => {
    const { address, addressLine2Label } = this.state;
    const { classes } = this.props;

    return (
      <div className={classes.billingAddressTextFieldContainer}>
        <TextField
          fullWidth
          InputProps={{
            classes: { input: classes.billingAddressInput, root: classes.billingAddressInputRoot },
          }}
          // label={addressLine2Label}
          InputLabelProps={{ shrink: true }}
          placeholder={addressLine2Label}
          variant="outlined"
          value={address.line2}
          onChange={this.onChangeAddressLine2}
        />
      </div>
    );
  };

  private maybeRenderStateInput = () => {
    const { address, stateLabel } = this.state;
    const { classes } = this.props;

    if (!isStateShown(address.country)) {
      return "";
    }

    return (
      <div className={classes.billingAddressTextFieldContainer}>
        <TextField
          fullWidth
          InputProps={{
            classes: { input: classes.billingAddressInput, root: classes.billingAddressInputRoot },
          }}
          // label={stateLabel}
          InputLabelProps={{ shrink: true }}
          placeholder={stateLabel}
          variant="outlined"
          value={address.state}
          onChange={this.onChangeState}
        />
      </div>
    );
  };

  private maybeRenderCityInput = () => {
    const { address, cityLabel } = this.state;
    const { classes } = this.props;

    return (
      <TextField
        fullWidth
        style={{ marginRight: 10 }}
        InputProps={{
          classes: { input: classes.billingAddressInput, root: classes.billingAddressInputRoot },
        }}
        // label={cityLabel}
        InputLabelProps={{ shrink: true }}
        placeholder={cityLabel}
        variant="outlined"
        value={address.city}
        onChange={this.onChangeCity}
      />
    );
  };

  private maybeRenderPostalCodeInput = () => {
    const { address, postalCodeLabel } = this.state;
    const { classes } = this.props;

    if (!isPostalCodeShown(address.country)) {
      return "";
    }

    return (
      <TextField
        fullWidth
        InputProps={{
          classes: { input: classes.billingAddressInput, root: classes.billingAddressInputRoot },
        }}
        // label={postalCodeLabel}
        InputLabelProps={{ shrink: true }}
        placeholder={postalCodeLabel}
        variant="outlined"
        value={address.postal_code}
        onChange={this.onChangePostalCode}
      />
    );
  };

  public render() {
    const { classes } = this.props;

    return (
      <div className={classes.container}>
        {this.maybeRenderAddressLine1Input()}
        {this.maybeRenderAddressLine2Input()}
        {this.maybeRenderStateInput()}
        <div className={classes.billingAddressTextFieldContainer}>
          {this.maybeRenderCityInput()}
          {this.maybeRenderPostalCodeInput()}
        </div>
        <CountryPicker onSelectCountry={this.onChangeCountry} />
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const LocalizedAddressFormStyled = withStyles(s.styles)(LocalizedAddressFormCmpt);

///////////
// Redux //
///////////

function mapStateToProps(_state: any, _ownProps: IContainer) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {})(LocalizedAddressFormStyled);

///////////
// Relay //
///////////

export const LocalizedAddressForm = createFragmentContainer<IContainer>(
  reduxContainer,
  localizedAddressFormFragments,
);
